import { useEffect, useRef, useState } from "react";
import { Alert, Button, Spin, Steps } from "antd";
import { Header } from "../../../components/Landing/Header/haeder.view";
import { Footer } from "../../../components/Landing/Footer";
import { BannerPostulacion } from "../components/BannerPostulacion";
import { useSeleccionarCategoria } from "../../../hooks/useSeleccionarCategoria";
import { useAgregarurl } from "../../../hooks/useAgregarurl";
import { usePersonaNatural } from "../../../hooks/usePersonaNatural";
import { useEncrypt } from "../../../hooks/useEncrypt";
import apiParticipantes from "../../../api/Participantes/api-participantes";
import { usePostulacionWizard } from "./usePostulacionWizard";
import { useAutoguardado } from "./useAutoguardado";
import { IndicadorGuardado } from "./IndicadorGuardado";
import {
  PASOS,
  estadoDeLosPasos,
  indiceDePaso,
  pasosCompletados,
} from "./pasosPostulacion";
import { useInscripcion } from "./useInscripcion";
import { ModalRecuento } from "./ModalRecuento";
import { ModalPostulacionExitosa } from "../components/ModalPostulacionExitosa";
import { convocatoriaCerrada } from "../../../utils/puedeInscribirse";
import {
  hayQueMostrarAvisos,
  loQueFaltaPorSubsanar,
  observacionesDeLoQueFalta,
} from "../../../utils/avisosPostulacion";
import { useAvisosPostulacion } from "./useAvisosPostulacion";
import { PanelAlertas } from "./PanelAlertas";
import { Paso1Perfil } from "./pasos/Paso1Perfil";
import { Paso2Documentos } from "./pasos/Paso2Documentos";
import { Paso3Propuesta } from "./pasos/Paso3Propuesta";
import "../Usuario.style.scss";

/**
 * La postulación en una sola página, con los tres pasos en un stepper.
 *
 * Los tres pasos se montan a la vez y solo se oculta el que no está activo,
 * para que ir y volver no borre lo que la persona ya escribió. Antes cada
 * paso era una página distinta y lo escrito viajaba por localStorage.
 *
 * Lo que se escribe se guarda solo, al salir de cada campo, para que no
 * dependa de acordarse de un botón.
 */
export const Postulacion = () => {
  const { decrypt } = useEncrypt();

  const {
    convocatoria,
    cargando,
    perfiles,
    paso,
    irAPaso,
    siguiente,
    anterior,
    enPrimerPaso,
    enUltimoPaso,
    tipoParticipante,
    cambiarTipoParticipante,
  } = usePostulacionWizard();

  const { categoria, cambioCategorias } = useSeleccionarCategoria();
  const { autoguardarParticipante } = usePersonaNatural();


  const {
    nombrepropuesta,
    cambiarnombrepropuesta,
    url,
    cambiarurl,
    agregarurl,
    urls,
    eliminarurl,
    getPostulacion,
    continuarurl,
    modal: inscripcionExitosa,
    AceptarModal,
    postulacion: postulacionInscrita,
  } = useAgregarurl();

  const { estado, texto, sembrar, guardar, reintentar } = useAutoguardado();

  const {
    postulacion: guardada,
    resumen: avisos,
    fechaDelInforme,
    fechaLimiteSubsanar,
    cargando: calculandoAvisos,
    recargando: revisandoAvisos,
    recargar: revisarDeNuevo,
  } = useAvisosPostulacion(convocatoria, paso, tipoParticipante);

  /*
    La aceptacion de politicas y terminos no se guarda: no vuelve del
    servidor, asi que se marca cada vez que se va a inscribir, que es
    cuando se declara.
  */
  const [aceptaCondiciones, setAceptaCondiciones] = useState(false);

  /**
   * Lo guardado, con lo que la persona acaba de escribir encima.
   *
   * El recuento no puede mirar solo el servidor: el autoguardado corre al
   * salir del campo y el nombre o la categoria recien puestos podrian no
   * haber llegado todavia. Se prefiere lo del formulario cuando lo hay, y
   * lo guardado cuando el formulario aun no lo tiene.
   */
  const propuestaActual = {
    ...(guardada ?? {}),
    nombre_propuesta: nombrepropuesta || guardada?.nombre_propuesta,
    categoria_linea_convocatoria:
      categoria ?? guardada?.categoria_linea_convocatoria,
    politicas: aceptaCondiciones,
  };

  /**
   * Inscribe, y deja escrito lo que quedo por subsanar.
   *
   * Lo administrativo que falta no impide inscribir —es subsanable—, pero si
   * tiene que constar. Se prediligencian las observaciones de la propuesta
   * con el mismo texto que la persona ve en su radicado: asi los dos leen lo
   * mismo y quien verifica no tiene que volver a redactarlo.
   *
   * Si eso falla no se le niega la inscripcion a nadie: ya quedo inscrita.
   */
  const inscribirPropuesta = async () => {
    const hecho = await continuarurl(false);

    const pendiente = observacionesDeLoQueFalta(avisos, fechaDelInforme, fechaLimiteSubsanar);
    if (pendiente) {
      try {
        await apiParticipantes.actualizarObservacion(idPostulacion(), pendiente);
      } catch (error) {
        console.error("No se pudieron prediligenciar las observaciones", error);
      }
    }

    return hecho;
  };

  const {
    recuentoAbierto,
    abrirRecuento,
    cerrarRecuento,
    impedimentos,
    inscribiendo,
    confirmar: confirmarInscripcion,
  } = useInscripcion(convocatoria, propuestaActual, inscribirPropuesta);

  /** Hasta que no se sepa qué tiene el servidor, no se escribe nada. */
  const sembrado = useRef(false);

  const idPostulacion = () => decrypt(localStorage.getItem("id_postulacion"));

  useEffect(() => {
    const cargar = async () => {
      const datos: any = await getPostulacion();
      sembrar("nombre", datos?.nombre_propuesta ?? "");
      sembrar("urls", datos?.links ?? []);
      sembrar("categoria", datos?.categoria_linea_convocatoria ?? null);
      // El hook de categoria arranca en null y nadie mas la carga, asi que
      // el select salia vacio aunque ya estuviera elegida y guardada. Se usa
      // cambioCategorias y no elegirCategoria: esto viene del servidor, no
      // hay nada que volver a guardar.
      if (datos?.categoria_linea_convocatoria !== null && datos?.categoria_linea_convocatoria !== undefined) {
        cambioCategorias(datos.categoria_linea_convocatoria);
      }
      sembrado.current = true;
    };
    cargar();
  }, []);

  /**
   * Los enlaces se guardan al agregarlos o quitarlos, no al salir de un campo,
   * porque el cambio ocurre al tocar el botón y no al perder el foco.
   */
  useEffect(() => {
    if (!sembrado.current) return;
    guardar("urls", urls, () =>
      apiParticipantes.guardarLinksPostulacion(idPostulacion(), urls)
    );
  }, [urls]);

  const guardarNombre = () => {
    if (!sembrado.current) return;
    guardar("nombre", nombrepropuesta, () =>
      apiParticipantes.actualizarNombrePropuesta(idPostulacion(), nombrepropuesta)
    );
  };

  /**
   * Los datos personales se guardan al salir de cualquier campo. El
   * formulario entrega lo que tiene escrito, porque el estado vive en su
   * propia instancia del hook.
   */
  const guardarDatosPersonales = (participante: any) => {
    if (!sembrado.current) return;
    guardar("participante", participante, () =>
      autoguardarParticipante(participante, tipoParticipante ?? undefined)
    );
  };

  const elegirCategoria = (valor: any) => {
    cambioCategorias(valor);
    if (!sembrado.current) return;
    guardar("categoria", valor, () =>
      apiParticipantes.actualizarCategoriaLineaConvocatoriaPropuesta(
        idPostulacion(),
        valor
      )
    );
  };

  /**
   * Cada paso se monta la primera vez que se visita, y se queda montado.
   *
   * Montar los tres de entrada disparaba a la vez las consultas de los
   * formularios de datos personales y de los dos grupos de documentos, un
   * aluvion de peticiones al abrir la pantalla. Dejarlos montados despues
   * es lo que hace que ir y volver no borre lo que ya se escribio.
   */
  const [visitados, setVisitados] = useState<number[]>([paso]);

  useEffect(() => {
    setVisitados((previos) =>
      previos.includes(paso) ? previos : [...previos, paso]
    );
  }, [paso]);

  /*
    Empezar la postulacion y recibir de entrada la lista de lo que te van a
    pedir subsanar no ayuda: todavia no has tenido ocasion de llenar nada.
    En la primera pasada los avisos esperan al tercer paso, que es donde la
    revision tiene sentido; quien vuelve a editar los ve desde el primero.
  */
  const mostrarAvisos = hayQueMostrarAvisos(propuestaActual, enUltimoPaso);

  /**
   * Cerrada la convocatoria no hay nada que editar aqui.
   *
   * Esto es para la pantalla, no es el control: quien quiera saltarselo
   * cambiando el reloj de su equipo se encuentra con que el servidor
   * comprueba el cierre con el suyo al guardar.
   */
  const cerrada = convocatoriaCerrada(convocatoria);

  /**
   * Elegir perfil siembra los documentos que le corresponden, asi que hay
   * que releer: los avisos dependen de lo que quede guardado.
   */
  const elegirPerfil = async (tipo: number) => {
    await cambiarTipoParticipante(tipo);
    revisarDeNuevo();
  };

  /** Solo se oculta: así el paso conserva lo que ya tenía escrito. */
  const visible = (indice: number) =>
    paso === indice ? undefined : ({ display: "none" } as const);

  return (
    <>
      <Header />
      <BannerPostulacion convocatorias={convocatoria} />

      <div className="container-fluid2">
        <div className="row mt-4">
          <div className="col-12 no-margin font-family-Montserrat-Regular font-size-18px">
            <span className="font-family-Montserrat-SemiBold">
              Estás a punto de postularte en la convocatoria.
            </span>{" "}
            Puedes moverte entre los pasos cuando quieras; lo que escribas se
            guarda solo.
          </div>
        </div>
      </div>

      {cerrada ? (
        <div className="container-fluid2 mt-4 mb-5">
          <Alert
            type="info"
            showIcon
            message={
              <span className="font-family-Montserrat-SemiBold font-size-16px">
                Esta convocatoria ya cerró
              </span>
            }
            description={
              <span className="font-family-Montserrat-Regular font-size-14px">
                Tu propuesta ya no se puede editar. Puedes consultarla desde
                “Mis proyectos”.
              </span>
            }
          />
        </div>
      ) : (
        <>
      {/* Los pasos van sobre una banda, para que se lean como una pieza
          aparte del contenido del paso que se esta llenando. */}
      <div className="banda-pasos-postulacion">
        <div className="container-fluid2 py-4">
        <div className="d-flex justify-content-end mb-2">
          <IndicadorGuardado estado={estado} texto={texto} reintentar={reintentar} />
        </div>

        <Steps current={paso} onChange={irAPaso} responsive>
          {PASOS.map((unPaso) => (
            <Steps.Step
              key={unPaso.clave}
              title={unPaso.titulo}
              description={unPaso.descripcion}
            />
          ))}
        </Steps>
        </div>
      </div>

      {mostrarAvisos && (
      <PanelAlertas
        resumen={avisos}
        cargando={calculandoAvisos}
        recargando={revisandoAvisos}
        fechaDelInforme={fechaDelInforme}
        fechaLimiteSubsanar={fechaLimiteSubsanar}
        pasoActual={PASOS[paso]?.clave}
        irAlPaso={(clave) => irAPaso(indiceDePaso(clave))}
      />
      )}

      {cargando ? (
        <div className="container-fluid2 mt-4 d-flex justify-content-center">
          <Spin />
        </div>
      ) : (
        <>
          <div style={visible(0)}>
            {visitados.includes(0) && (
            <Paso1Perfil
              convocatoria={convocatoria}
              perfiles={perfiles}
              tipoParticipante={tipoParticipante}
              cambiarTipoParticipante={elegirPerfil}
              alSalirDeUnCampo={guardarDatosPersonales}
            />
            )}
          </div>

          <div style={visible(1)}>
            {visitados.includes(1) && (
            <Paso2Documentos
              convocatoria={convocatoria}
              categoria={categoria}
              cambioCategorias={elegirCategoria}
              tipoParticipante={tipoParticipante}
              alCambiarDocumentos={revisarDeNuevo}
            />
            )}
          </div>

          <div style={visible(2)}>
            {visitados.includes(2) && (
            <Paso3Propuesta
              nombrepropuesta={nombrepropuesta}
              cambiarnombrepropuesta={cambiarnombrepropuesta}
              alSalirDelNombre={guardarNombre}
              url={url}
              cambiarurl={cambiarurl}
              agregarurl={agregarurl}
              urls={urls}
              eliminarurl={eliminarurl}
              aceptaCondiciones={aceptaCondiciones}
              alAceptarCondiciones={setAceptaCondiciones}
            />
            )}
          </div>
        </>
      )}

      <div className="container-fluid2 mt-4 mb-5">
        <div className="d-flex justify-content-between">
          <Button
            className="button-blanco-radius font-size-12px"
            onClick={anterior}
            disabled={enPrimerPaso}
          >
            Atrás
          </Button>
          {enUltimoPaso ? (
            <Button
              className="button-azul-radius font-size-12px"
              onClick={abrirRecuento}
            >
              Inscribir propuesta
            </Button>
          ) : (
            <Button
              className="button-azul-radius font-size-12px"
              onClick={siguiente}
            >
              Siguiente
            </Button>
          )}
        </div>
      </div>

        </>
      )}

      <ModalRecuento
        visible={recuentoAbierto}
        pasos={estadoDeLosPasos(pasosCompletados(propuestaActual))}
        impedimentos={impedimentos}
        resumen={avisos}
        inscribiendo={inscribiendo}
        alCerrar={cerrarRecuento}
        alConfirmar={confirmarInscripcion}
        irAlPaso={(clave) => irAPaso(indiceDePaso(clave))}
      />

      <ModalPostulacionExitosa
        visible={inscripcionExitosa}
        postulacion={postulacionInscrita}
        convocatoria={convocatoria}
        nombrepropuesta={nombrepropuesta}
        pendientes={loQueFaltaPorSubsanar(avisos)}
        fechaDelInforme={fechaDelInforme}
        fechaLimiteSubsanar={fechaLimiteSubsanar}
        alAceptar={AceptarModal}
      />

      <Footer />
    </>
  );
};
